import { toaster } from "@becon-team/becon-fe-lib/my-toast";
import { throttle } from "@becon-team/becon-fe-lib/utils";

import { platform } from "./platform";
import { setAuth } from "./setAuth";

import type { TFunction } from "i18next";

const GO_TO_ACTIVITY = "goToActivity";

type ScalpCandidateTag = "COMPOSITE_SCALP" | "COMPARISON_ANALYSIS" | "PLAIN";

export type GoToActivityAction =
  | "DevicePairingBluetooth"
  | "ScalpLookAround"
  | "ScalpDiagnosis"
  | "SMP"
  | "MDiagnosis"
  | "SkinMicroDiagnosis"
  | "SkinMacroDiagnosis"
  | "ScalpCrownDiagnosis"
  | "DevicePairingCable"
  | "DevicePairingWifi";

/** 특정 네이티브 페이지로 이동
 *
 * @param path 액션 후 되돌아오는 경로
 * @param nextAction "DevicePairingBluetooth" 후 진행할 액션
 * @param userId 분석에 필요한 유저 아이디
 * @param userNickname 분석에 필요한 유저 닉네임
 * @param reportId 분석에 필요한 리포트 아이디
 * @param tag "ScalpDiagnosis"을 어느 용도로 촬영했는지 알려주는 태그
 * @param compositeScalp "ScalpDiagnosis"에서 "compositeScalp"에 후보를 추가하기 위한 정보
 * @param comparisonSet "ScalpDiagnosis"에서 "comparisonSet"에 후보를 추가하기 위한 정보
 * @param hairMacroSet "SMP"나 "MDiagnosis"에서 후보를 추가하기 위한 정보
 * @param skinSetId  "SkinMacroDiagnosis"에 후보를 추가하기 위한 정보
 */
export const _goToActivity = (
  t: TFunction,
  action: GoToActivityAction,
  information?: {
    path?: string;
    nextAction?: GoToActivityAction;
    userId: string;
    userNickname: string;
    reportId?: number;
    tag?: ScalpCandidateTag;
    compositeScalp?: {
      compositeScalpId: number;
      candidateIds: number[];
    };
    comparisonSet?: {
      comparisonSetId: number;
      analysisId: number;
    };
    hairMacroSet?: {
      type: "CROWN" | "FRONT";
    };
    skinSetId?: number;
  },
) => {
  const {
    path,
    nextAction,
    userId,
    reportId,
    tag,
    compositeScalp,
    comparisonSet,
    hairMacroSet,
    skinSetId,
    userNickname,
  } = information || {};

  userId &&
    userNickname &&
    setAuth({
      user_id: userId,
      user_nickname: userNickname,
    });

  const activityParams = JSON.stringify({
    name: action,
    information: {
      // eslint-disable-next-line no-restricted-globals
      path: path ?? location.pathname,
      nextAction: nextAction ?? null,
      userId: userId ?? null,
      reportId: reportId ?? null,
      tag: tag ?? null,
      compositeScalp: compositeScalp ?? null,
      comparisonSet: comparisonSet ?? null,
      hairMacroSet: hairMacroSet ?? null,
      skinSetId: skinSetId ?? null,
    },
  });

  switch (platform) {
    case "android":
      try {
        window.Becon.requestAction(GO_TO_ACTIVITY, action, activityParams);
      } catch (err) {
        alert(`android error -> ${err}`);
      }
      break;
    case "ios":
      try {
        window.webkit.messageHandlers.goTo.postMessage(activityParams);
      } catch (err) {
        alert(`ios error -> ${err}`);
      }
      break;
    case "web":
      toaster.warning(t("앱에서만 사용 가능한 기능이에요."));
      break;
  }
};

export const goToActivity = throttle(_goToActivity, 1000);
